import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    List,
    makeStyles,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useQuery, useLazyQuery } from '@apollo/client';
import { FetchPolicy, MEDIA_QUERY } from 'utils/enum/Core';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import GeneralUtils from 'utils/GeneralUtils';
import ROLE, { AGENT_TYPE } from 'utils/enum/OpenAIEnum';
import AIQuery from 'services/graphQL/query/ai/AIQuery';
import AIConversationHeader from 'components/modules/crm/ai/AIConversationHeader';
import InfiniteScroll from 'components/widgets/InfiniteScroll';
import CustomerSection from 'components/modules/messageCenter/list/CustomerSection';
import AIStyle from 'styles/modules/ai/AIStyle';

const aiStyles = makeStyles((theme) => AIStyle.content(theme));
const useStyles = makeStyles((theme) => ({
    main: {
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
        [`@media (max-width: ${MEDIA_QUERY.W_992}px)`]: {
            flexDirection: 'column',
        },
    },
    containerSidebar: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flexShrink: 0,
        backgroundColor: theme.palette.background.primary,
        [`@media (max-width: ${MEDIA_QUERY.W_992}px)`]: {
            maxHeight: 400,
            marginBottom: 15,
        },
    },
    box: {
        width: 320,
        minWidth: 320,
        padding: theme.spacing(3),
        overflow: 'auto',
        paddingTop: 0,
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            width: '100%',
            minWidth: 'unset',
            padding: theme.spacing(2),
        },
    },
    list: {
        paddingTop: '20px',
    },
    noConversations: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 18,
    },
    audio: {
        top: 10,
        right: 20,
        position: 'sticky',
        textAlign: 'right',
        zIndex: 150,
        marginBottom: 10,
    },
}));

const AIConversations = ({ type }) => {
    const classes = useStyles();
    const aiClasses = aiStyles();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(MEDIA_QUERY.W_992));

    const [state, setState] = useState({
        conversations: [],
        selectedConversation: null,
        selectedConversationDetails: null,
        params: {
            init: 0,
            limit: 50,
        },
        requestMoreData: true,
        recordingURL: null,
    });

    const {
        params,
        requestMoreData,
        conversations,
        selectedConversation,
        selectedConversationDetails,
        recordingURL,
    } = state;

    const {
        data,
        loading,
        error,
    } = useQuery(AIQuery.GET_AI_CONVERSATIONS_HISTORY, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        variables: {
            conversationType: type,
            agentType: AGENT_TYPE.CRM_AGENT,
            paginate: {
                init: params.init,
                limit: params.limit,
            },
        },
    });

    const [getConversationDetails, { loading: loadingConversationDetails }] = useLazyQuery(AIQuery.GET_AI_CONVERSATION_DETAILS, {
        onCompleted: async (response) => {
            if (response) {
                const details = response.getAIConversationDetails;
                setState((prevState) => ({
                    ...prevState,
                    selectedConversationDetails: details,
                }));

                if (!StringUtils.isEmpty(selectedConversation.recordingURL)) {
                    ModalUtils.infoMessage(null, 'Downloading the call recording. Please wait...');

                    const fileURL = await GeneralUtils.downloadRecordingFile(selectedConversation.id);
                    setState((prevState) => ({
                        ...prevState,
                        recordingURL: fileURL,
                    }));
                }
            }
        },
        onError: (err) => {
            ModalUtils.errorMessage([err]);
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && data) {
            const records = data.getAIConversationsHistory;
            if (records) {
                setState((prevState) => ({
                    ...prevState,
                    conversations: [...prevState.conversations, ...records],
                    requestMoreData: records.length === params.limit,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    const loadMore = () => {
        if (!requestMoreData) return;

        const start = conversations.length;
        setState((prevState) => ({
            ...prevState,
            params: {
                ...prevState.params,
                init: start,
            },
        }));
    };

    const setSelectedConversation = (conversation) => {
        setState((prevState) => ({
            ...prevState,
            selectedConversation: conversation,
            selectedConversationDetails: null,
            recordingURL: null,
        }));

        getConversationDetails({
            variables: {
                type,
                id: conversation.id,
            },
        });
    };

    const noConversations = !loading && conversations.length === 0;
    return (
        <div className={classes.main}>
            {noConversations && (
                <div className={classes.noConversations}>No conversations found.</div>
            )}
            {!noConversations && (
                <div className={classes.containerSidebar}>
                    <InfiniteScroll
                        load={loading}
                        loadMore={loadMore}
                        className={classes.box}
                        loadAtScrollPercent={70}
                        totalRecord={Infinity}
                        lengthRecord={conversations.length}
                    >
                        <List className={classes.list}>
                            {conversations.map((item) => (
                                <AIConversationHeader
                                    type={type}
                                    record={item}
                                    key={item.id}
                                    selectedConversationId={selectedConversation?.id}
                                    setSelectedConversation={setSelectedConversation}
                                />
                            ))}
                        </List>
                    </InfiniteScroll>
                </div>
            )}
            {(!loadingConversationDetails && selectedConversationDetails) && (
                <>
                    <div className={aiClasses.messagesContainer}>
                        <div className={classes.audio}>
                            {!StringUtils.isEmpty(recordingURL) && (
                                // eslint-disable-next-line jsx-a11y/media-has-caption
                                <audio controls src={recordingURL} />
                            )}
                        </div>
                        {selectedConversationDetails.messages.map((item, index) => {
                            const isUser = item.role === ROLE.USER;
                            return (
                                <div key={index} className={clsx(aiClasses.main, { [aiClasses.assistantMessageBox]: !isUser })}>
                                    <div className={clsx(aiClasses.message, isUser ? aiClasses.userMessage : aiClasses.assistantMessage)}>
                                        <Typography className={aiClasses.text}>
                                            {item.message}
                                        </Typography>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {!StringUtils.isEmpty(selectedConversation.crmId) && !isMobile && (
                        <CustomerSection
                            customer={{
                                firstName: selectedConversation.firstName,
                                lastName: selectedConversation.lastName,
                                email: selectedConversation.email,
                                customerId: selectedConversation.customerId,
                                opportunity: {
                                    crmId: selectedConversation.crmId,
                                    leadCode: selectedConversation.leadCode,
                                },
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

AIConversations.propTypes = {
    type: PropTypes.string.isRequired,
};

export default AIConversations;
